import React, { useMemo, useState } from 'react'
import { useI18n } from '../../../i18n'
import type { ControlOverviewRow } from '../../../services/controlCentricWorkflowApi'
import listStyles from '../../../styles/pages/governance/GovernanceListing.module.scss'
import ccStyles from '../../../styles/pages/compliance-center/ComplianceCenterOverview.module.scss'
import {
  filterControlsByRemediationTab,
  type RemediationFilterTab,
} from '../../../pages/compliance-center/journeyRemediationNav'

type Props = {
  controls: ControlOverviewRow[]
  selectedKey: string | null
  onSelect: (key: string) => void
  remediationMode?: boolean
  remediationFilter?: RemediationFilterTab
}

const FILTER_TABS: RemediationFilterTab[] = ['ALL', 'FAILED', 'PARTIAL', 'EVIDENCE_GAP', 'PENDING_EVIDENCE', 'NEEDS_REVIEW']

export function ControlCentricControlList({
  controls,
  selectedKey,
  onSelect,
  remediationMode = false,
  remediationFilter: remediationFilterProp = 'ALL',
}: Props) {
  const { t } = useI18n()
  const [filterTab, setFilterTab] = useState<RemediationFilterTab>(remediationFilterProp)

  const visibleControls = useMemo(() => {
    if (!remediationMode) return controls
    return filterControlsByRemediationTab(
      controls.map((c) => ({
        ...c,
        status: c.evidenceSufficiency === 'INSUFFICIENT' ? 'EVIDENCE_GAP' : c.controlStatus,
        unresolvedStatus: c.controlStatus,
      })),
      filterTab,
    )
  }, [controls, remediationMode, filterTab])

  return (
    <section className={listStyles.panel}>
      <h3 className={listStyles.panelExternalTitle}>{t('controlCentric.controls.list', 'Controls')}</h3>
      {remediationMode ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                filterTab === tab ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
              }`}
              onClick={() => setFilterTab(tab)}
            >
              {tab.replace('_', ' ')}
            </button>
          ))}
        </div>
      ) : null}
      <ul className="mt-3 max-h-[32rem] space-y-2 overflow-y-auto">
        {visibleControls.map((c) => {
          const active = selectedKey === c.controlKey
          return (
            <li key={c.controlKey}>
              <button
                type="button"
                className={`${ccStyles.questionPickCard} w-full px-3 py-2.5 text-left transition ${
                  active ? `${ccStyles.questionPickCardActive} border-green-600 bg-green-50` : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                onClick={() => onSelect(c.controlKey)}
              >
                <div className={`${ccStyles.questionPickTitle} font-mono text-[11px] text-slate-600`}>{c.controlKey}</div>
                <div className="mt-0.5 text-sm font-medium text-slate-900">{c.title}</div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {c.questionsAnswered}/{c.questionCount} · {c.evidenceSufficiency || '—'}
                </div>
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
